/**
 * Message templates.
 *
 * A deliberately small `{{variable}}` renderer rather than a templating library. Agency staff
 * write these templates themselves, so the language has to be explainable in one line of help
 * text, and it must be impossible for a mistyped template to throw an exception in the middle
 * of a reminder run and stop the queue.
 *
 * Unknown variables therefore render as an empty string and are reported separately, so the
 * template editor can warn about them while the send still goes out.
 */

export type TemplateChannel = "email" | "sms" | "whatsapp" | "in_app";

export interface TemplateVariable {
  name: string;
  description: string;
  example: string;
}

/** Everything a template may reference. The editor shows this list; the renderer fills it. */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  { name: "tenant.name", description: "The tenant's full name", example: "Adaeze Nwosu" },
  { name: "tenant.first_name", description: "The tenant's first name", example: "Adaeze" },
  { name: "tenant.email", description: "The tenant's email address", example: "adaeze@example.com" },
  { name: "tenant.phone", description: "The tenant's primary phone", example: "+234 803 000 0000" },
  { name: "unit.label", description: "The unit, as you named it", example: "Flat 3B" },
  { name: "property.name", description: "The property name", example: "Palm Court" },
  { name: "property.address", description: "The property address", example: "14 Bourdillon Road, Ikoyi" },
  { name: "lease.start_date", description: "Tenancy start date", example: "1 Mar 2026" },
  { name: "lease.end_date", description: "Tenancy end date", example: "28 Feb 2027" },
  { name: "lease.rent", description: "Rent for one cycle", example: "₦3,500,000.00" },
  { name: "lease.cycle", description: "How often rent falls due", example: "Annual" },
  { name: "lease.days_to_expiry", description: "Days until the tenancy ends", example: "30" },
  { name: "invoice.number", description: "Invoice number", example: "INV-000142" },
  { name: "invoice.total", description: "Invoice total", example: "₦3,500,000.00" },
  { name: "invoice.balance", description: "What is still outstanding", example: "₦1,200,000.00" },
  { name: "invoice.due_date", description: "When it falls due", example: "1 Mar 2026" },
  { name: "invoice.days_overdue", description: "Days past the due date", example: "9" },
  // Both are served by the agency application, not by the listing site — `appUrl()` builds them.
  { name: "pay_link", description: "A link to pay this invoice online", example: "https://app.rentor.ng/pay/AB12CD34" },
  { name: "portal_link", description: "A link to the tenant portal", example: "https://app.rentor.ng/portal" },
  { name: "agency.name", description: "Your agency's name", example: "Broadview Estates" },
  { name: "agency.phone", description: "Your agency's phone number", example: "+234 802 111 2222" },
  { name: "agency.email", description: "Your agency's email address", example: "hello@broadview.ng" },
  { name: "today", description: "Today's date", example: "19 Aug 2026" },
];

export type TemplateContext = Record<string, unknown>;

const TOKEN = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/** Read `a.b.c` out of a nested context object without throwing on a missing branch. */
export function lookup(context: TemplateContext, path: string): unknown {
  let cursor: unknown = context;
  for (const key of path.split(".")) {
    if (cursor === null || cursor === undefined || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

export interface RenderResult {
  text: string;
  /** Variables the template used that the context could not fill. Surfaced in the editor. */
  missing: string[];
}

export function render(template: string, context: TemplateContext): RenderResult {
  const missing = new Set<string>();
  const text = template.replace(TOKEN, (_match, path: string) => {
    const value = lookup(context, path);
    if (value === undefined || value === null || value === "") {
      missing.add(path);
      return "";
    }
    return String(value);
  });
  return { text, missing: [...missing] };
}

/** Every variable a template references, for validation before it is saved. */
export function variablesUsed(template: string): string[] {
  const found = new Set<string>();
  for (const match of template.matchAll(TOKEN)) found.add(match[1]);
  return [...found];
}

/** Variables that are not in the known list — almost always a typo, so the editor flags them. */
export function unknownVariables(template: string): string[] {
  const known = new Set(TEMPLATE_VARIABLES.map((v) => v.name));
  return variablesUsed(template).filter((v) => !known.has(v));
}

/** A context of examples, so the template editor can preview without touching a real tenancy. */
export function sampleContext(): TemplateContext {
  const context: TemplateContext = {};
  for (const variable of TEMPLATE_VARIABLES) {
    const parts = variable.name.split(".");
    let cursor = context;
    parts.forEach((part, i) => {
      if (i === parts.length - 1) cursor[part] = variable.example;
      else cursor = (cursor[part] ??= {}) as TemplateContext;
    });
  }
  return context;
}

/**
 * SMS is charged per 160-character segment (70 if any character is non-GSM), so the editor
 * shows the segment count live. A three-segment reminder to 400 tenants is a real bill.
 */
export function smsSegments(text: string): { characters: number; segments: number; unicode: boolean } {
  const unicode = /[^\x20-\x7E\n\r]/.test(text);
  const perSegment = unicode ? 70 : 160;
  const perConcatSegment = unicode ? 67 : 153;
  const characters = text.length;
  const segments =
    characters === 0 ? 0 : characters <= perSegment ? 1 : Math.ceil(characters / perConcatSegment);
  return { characters, segments, unicode };
}

/** Escape a rendered value before it goes into an HTML email body. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Plain-text template body to a simple, well-supported HTML email body. */
export function textToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((para) => `<p style="margin:0 0 16px 0;line-height:1.6">${escapeHtml(para).replace(/\n/g, "<br>")}</p>`)
    .join("\n");
}
